import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface Plan {
  id: string;
  code: string;
  name: string;
  type: string;
  base_minutes: number;
  price_cents: number;
  history_visible_days: number;
  audio_retention_days: number;
  transcript_retention_days: number;
  note_retention_days: number;
  max_devices: number;
  max_templates: number | null;
  max_recording_minutes: number;
  is_trial: boolean;
  slug?: string;
  features?: Record<string, any> | null;
  soft_limit_minutes?: number;
}

interface Subscription {
  id: string;
  owner_id: string;
  plan_id: string;
  status: string;
  current_period_start: string;
  current_period_end: string;
  plan?: Plan;
}

interface UsageCycle {
  id: string;
  subscription_id: string;
  year_month: string;
  minutes_allocated: number;
  minutes_used: number;
  hard_limit_exceeded: boolean;
}

export function useSubscription() {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [usageCycle, setUsageCycle] = useState<UsageCycle | null>(null);
  const [plan, setPlan] = useState<Plan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [avgConsultationMinutes, setAvgConsultationMinutes] = useState<number>(0);
  const subscriptionIdRef = useRef<string | null>(null);

  const fetchSubscriptionData = useCallback(async () => {
    if (!user) {
      setSubscription(null);
      setUsageCycle(null);
      setPlan(null);
      subscriptionIdRef.current = null;
      setLoading(false);
      return;
    }

    // Helper to check clinic membership fallback
    const tryClinicFallback = async (): Promise<Plan | null> => {
      try {
        const { data: membership } = await supabase
          .from('clinic_members')
          .select('clinic_owner_id')
          .eq('member_id', user.id)
          .eq('status', 'active')
          .maybeSingle();
        
        if (membership?.clinic_owner_id) {
          const { data: ownerSub } = await supabase
            .from('subscriptions')
            .select('*, plans (*)')
            .eq('owner_id', membership.clinic_owner_id)
            .eq('status', 'active')
            .maybeSingle();
          
          if (ownerSub?.plans) {
            return ownerSub.plans as unknown as Plan;
          }
        }
      } catch {
        // ignore clinic fallback errors
      }
      return null;
    };

    const createVirtualUsageCycle = (selectedPlan: Plan): UsageCycle => ({
      id: 'virtual',
      subscription_id: 'none',
      year_month: new Date().toISOString().slice(0, 7),
      minutes_allocated: selectedPlan.base_minutes,
      minutes_used: 0,
      hard_limit_exceeded: false,
    });

    try {
      setError(null);
      // Fetch subscription with plan (active first, then any status)
      let { data: subData, error: subError } = await supabase
        .from('subscriptions')
        .select(`
          *,
          plans (*)
        `)
        .eq('owner_id', user.id)
        .eq('status', 'active')
        .maybeSingle();

      if (subError) {
        console.error('Error fetching subscription:', subError);
        setError(subError.message);
        return;
      }

      // If no active subscription, try to get any subscription for this user
      if (!subData) {
        const { data: anySubData } = await supabase
          .from('subscriptions')
          .select(`*, plans (*)`)
          .eq('owner_id', user.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (!anySubData) {
          // Try clinic membership fallback before using generic plan
          const clinicPlan = await tryClinicFallback();
          if (clinicPlan) {
            setSubscription(null);
            subscriptionIdRef.current = null;
            setPlan(clinicPlan);
            setUsageCycle(createVirtualUsageCycle(clinicPlan));
            return;
          }

          const { data: fallbackPlans } = await supabase
            .from('plans')
            .select('*')
            .eq('is_active', true)
            .neq('type', 'addon')
            .order('price_cents', { ascending: true });

          const fallbackPlan = (fallbackPlans || []).find((candidate) => candidate.code === 'TRIAL_60') || fallbackPlans?.[0] || null;

          setSubscription(null);
          subscriptionIdRef.current = null;

          if (fallbackPlan) {
            setPlan(fallbackPlan as unknown as Plan);
            setUsageCycle(createVirtualUsageCycle(fallbackPlan as unknown as Plan));
          } else {
            setPlan(null);
            setUsageCycle(null);
          }
          return;
        }

        // Has a non-active subscription (e.g. pending) — show it
        setSubscription(anySubData);
        setPlan(anySubData.plans as unknown as Plan);
        subscriptionIdRef.current = anySubData.id;
      }

      if (subData) {
        setSubscription(subData);
        setPlan(subData.plans as unknown as Plan);
        subscriptionIdRef.current = subData.id;

        // Fetch current usage cycle
        const currentYearMonth = new Date().toISOString().slice(0, 7);
        let { data: cycleData, error: cycleError } = await supabase
          .from('usage_cycles')
          .select('*')
          .eq('subscription_id', subData.id)
          .eq('year_month', currentYearMonth)
          .maybeSingle();

        if (cycleError) {
          console.error('Error fetching usage cycle:', cycleError);
        }

        // If no cycle exists for current month, create it automatically
        if (!cycleData && subData.plans) {
          const planData = subData.plans as unknown as Plan;
          
          // Try to create the cycle in the database
          const { data: newCycle, error: createError } = await supabase
            .from('usage_cycles')
            .insert({
              subscription_id: subData.id,
              year_month: currentYearMonth,
              minutes_allocated: planData.base_minutes,
              minutes_used: 0,
              hard_limit_exceeded: false,
            })
            .select()
            .single();

          if (createError) {
            // If creation fails (e.g., RLS or duplicate), use virtual cycle
            console.warn('Could not create usage cycle, using virtual:', createError.message);
            setUsageCycle({
              id: 'virtual',
              subscription_id: subData.id,
              year_month: currentYearMonth,
              minutes_allocated: planData.base_minutes,
              minutes_used: 0,
              hard_limit_exceeded: false,
            });
          } else {
            // Use the newly created cycle
            setUsageCycle(newCycle);
            console.log('Created new usage cycle for', currentYearMonth);
          }
        } else {
          setUsageCycle(cycleData);
        }
      }

      // Fetch average consultation duration for this user
      const { data: avgData } = await supabase
        .from('consultations')
        .select('audio_duration_seconds')
        .eq('doctor_id', user.id)
        .not('audio_duration_seconds', 'is', null)
        .gt('audio_duration_seconds', 0);

      if (avgData && avgData.length > 0) {
        const totalSeconds = avgData.reduce((sum, c) => sum + (c.audio_duration_seconds || 0), 0);
        const avgMinutes = (totalSeconds / avgData.length) / 60;
        setAvgConsultationMinutes(Math.round(avgMinutes * 10) / 10); // Round to 1 decimal
      }
    } catch (err) {
      console.error('Error in fetchSubscriptionData:', err);
      setError(err instanceof Error ? err.message : 'Erro ao carregar dados de assinatura');
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Initial fetch
  useEffect(() => {
    fetchSubscriptionData();
  }, [fetchSubscriptionData]);

  // Realtime subscription para atualizações automáticas após pagamento
  useEffect(() => {
    if (!user) return;

    // Canal para escutar mudanças na subscription do usuário
    const subscriptionChannel = supabase
      .channel('subscription-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'subscriptions',
          filter: `owner_id=eq.${user.id}`,
        },
        (payload) => {
          console.log('Subscription changed:', payload.eventType);
          // Refetch completo para garantir que todos os dados estejam sincronizados
          fetchSubscriptionData();
        }
      )
      .subscribe();

    // Canal para escutar mudanças no usage_cycle
    const usageCycleChannel = supabase
      .channel('usage-cycle-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'usage_cycles',
        },
        (payload) => {
          const changedCycle = payload.new as UsageCycle;
          if (subscriptionIdRef.current && changedCycle.subscription_id === subscriptionIdRef.current) {
            console.log('Usage cycle changed:', payload.eventType);
            setUsageCycle(changedCycle);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscriptionChannel);
      supabase.removeChannel(usageCycleChannel);
    };
  }, [user, fetchSubscriptionData]);

  const remainingMinutes = usageCycle 
    ? Math.max(usageCycle.minutes_allocated - usageCycle.minutes_used, 0)
    : 0;

  const secondsRemaining = remainingMinutes * 60;

  const usagePercentage = usageCycle 
    ? Math.min((usageCycle.minutes_used / usageCycle.minutes_allocated) * 100, 100)
    : 0;

  const canRecord = loading || remainingMinutes > 0;

  // Check if user has enough minutes for their average consultation
  const hasEnoughForAvgConsultation = avgConsultationMinutes === 0 || remainingMinutes >= avgConsultationMinutes;

  return {
    subscription,
    usageCycle,
    plan,
    loading,
    error,
    remainingMinutes,
    secondsRemaining,
    usagePercentage,
    canRecord,
    avgConsultationMinutes,
    hasEnoughForAvgConsultation,
    refetch: fetchSubscriptionData,
  };
}
